import React, { useState, useEffect, useCallback } from 'react';
import { message, Tooltip } from 'antd';
import { DownloadOutlined, FileTextOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { Modal, Table, Button, Text as UIText, Group, Stack, Center, ScrollArea, Pagination } from '@good/ui/core';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores/rematch/root-store';
import moment from 'moment';
import apiClient from 'utilities/api-client';

interface ICreditNote {
  creditNoteId: string;
  creditNoteNumber: number;
  invoiceNumber: string;
  exportedDate: string;
  status: string;
  paymentSourceType: string;
  emailTo: string;
  invoiceTotal: number;
  debtorName: string;
  serviceProviderName: string;
  customerFirstName: string;
  customerLastName: string;
}

interface ICreditNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber?: string;
}

interface ICreditNotesResponse {
  creditNotes: ICreditNote[];
  total: number;
}

const CreditNotesModal: React.FC<ICreditNotesModalProps> = ({ isOpen, onClose, invoiceNumber }) => {
  const serviceProviderId = useSelector((state: IRootState) => state.authStore.portalUser?.serviceProviderId);
  const [creditNotes, setCreditNotes] = useState<ICreditNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchCreditNotes = useCallback(
    async (page = 1) => {
      if (!serviceProviderId) {
        return;
      }

      setLoading(true);

      try {
        const response = await apiClient.post('/api/portal/billing/credit-notes/list', {
          serviceProviderId,
          invoiceNumber,
          page,
          pageSize: pagination.pageSize,
        });

        const responseData = response.data as ICreditNotesResponse;

        if (response.status === 200) {
          setCreditNotes(responseData?.creditNotes || []);
          setPagination((prev) => ({
            ...prev,
            current: page,
            total: responseData?.total || 0,
          }));
        }
      } catch (error) {
        message.error('Failed to load credit notes');
      } finally {
        setLoading(false);
      }
    },
    [serviceProviderId, invoiceNumber, pagination.pageSize],
  );

  useEffect(() => {
    if (isOpen && serviceProviderId) {
      fetchCreditNotes();
    }
  }, [isOpen, serviceProviderId, fetchCreditNotes]);

  const handleDownload = useCallback(async (creditNoteId: string, creditNoteNumber: number) => {
    try {
      const response = await apiClient.get(`/api/portal/billing/credit-notes/${creditNoteId}/download`, {
        responseType: 'blob',
      });

      if (response.status === 200) {
        const blobData = response.data as Blob;

        if (!blobData || blobData.size === 0) {
          throw new Error('Received empty PDF file');
        }

        // Validate PDF header
        const arrayBuffer = await blobData.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const pdfHeader = String.fromCharCode(
          uint8Array[0] || 0,
          uint8Array[1] || 0,
          uint8Array[2] || 0,
          uint8Array[3] || 0,
        );

        if (pdfHeader !== '%PDF') {
          const textContent = new TextDecoder().decode(arrayBuffer);
          throw new Error(`Downloaded file is not a valid PDF. Received: ${textContent.substring(0, 100)}...`);
        }

        // Create and download the PDF
        const pdfBlob = new Blob([blobData], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Credit-Note-${creditNoteNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('Credit note downloaded successfully');
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      message.error(`Failed to download credit note: ${errorMessage}`);
    }
  }, []);

  const handleTableChange = useCallback(
    (pagination: { current?: number }) => {
      fetchCreditNotes(pagination.current || 1);
    },
    [fetchCreditNotes],
  );

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'SUCCESS':
        return '#52c41a';
      case 'PENDING':
        return '#faad14';
      case 'FAILED':
        return '#ff4d4f';
      default:
        return '#8c8c8c';
    }
  }, []);

  const getStatusText = useCallback((status: string) => {
    switch (status) {
      case 'SUCCESS':
        return 'Sent';
      case 'PENDING':
        return 'Pending';
      case 'FAILED':
        return 'Failed';
      default:
        return status;
    }
  }, []);

  const columns: Array<{
    title: string;
    dataIndex?: keyof ICreditNote;
    key: string;
    width?: number;
    render?: (value: unknown, record?: ICreditNote) => React.ReactNode;
  }> = [
    {
      title: 'Credit Note #',
      dataIndex: 'creditNoteNumber',
      key: 'creditNoteNumber',
      width: 120,
      render: (value: unknown) => (
        <UIText size='sm' fw={600}>
          {value as number}
        </UIText>
      ),
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 120,
      render: (value: unknown) => <UIText size='sm'>{value as string}</UIText>,
    },
    {
      title: 'Customer',
      dataIndex: 'customerFirstName',
      key: 'customerName',
      width: 200,
      render: (_value: unknown, record?: ICreditNote) => {
        const customerName = record ? `${record.customerFirstName || ''} ${record.customerLastName || ''}`.trim() : '';
        return (
          <Group gap='xs' align='center'>
            <UserOutlined style={{ color: '#8c8c8c' }} />
            <UIText size='sm'>{customerName || 'N/A'}</UIText>
          </Group>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'invoiceTotal',
      key: 'invoiceTotal',
      width: 100,
      render: (value: unknown) => (
        <UIText size='sm' fw={600}>
          ${(value as number)?.toFixed(2) || '0.00'}
        </UIText>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'exportedDate',
      key: 'exportedDate',
      width: 120,
      render: (value: unknown) => (
        <Group gap='xs' align='center'>
          <CalendarOutlined style={{ color: '#8c8c8c' }} />
          <UIText size='sm'>{moment(value as string).format('DD/MM/YYYY')}</UIText>
        </Group>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: unknown) => {
        const status = value as string;
        return (
          <UIText
            size='xs'
            fw={600}
            style={{
              color: getStatusColor(status),
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: `${getStatusColor(status)}15`,
              display: 'inline-block',
            }}
          >
            {getStatusText(status)}
          </UIText>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_value: unknown, record?: ICreditNote) => (
        <Tooltip title='Download Credit Note'>
          <Button
            variant='subtle'
            size='xs'
            onClick={() => record && handleDownload(record.creditNoteId, record.creditNoteNumber)}
            leftSection={<DownloadOutlined />}
          >
            Download
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title='Credit Notes'
      size='xl'
      variant='desktop'
      scrollable
      maxHeight={600}
    >
      <Stack gap='md'>
        <UIText size='sm' c='dimmed'>
          View and download credit notes for processed invoices
        </UIText>

        {creditNotes.length === 0 && !loading ? (
          <Center py='xl'>
            <Stack align='center' gap='md'>
              <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <Stack align='center' gap='xs'>
                <UIText size='lg' c='dimmed'>
                  No credit notes found
                </UIText>
                <UIText size='sm' c='dimmed' ta='center'>
                  There are no credit notes available for this service provider. This may be because the credit notes
                  table hasn't been migrated to the development database yet.
                </UIText>
              </Stack>
            </Stack>
          </Center>
        ) : (
          <>
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    {columns.map((column) => (
                      <Table.Th key={column.key} style={{ width: column.width }}>
                        {column.title}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading ? (
                    <Table.Tr>
                      <Table.Td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        Loading...
                      </Table.Td>
                    </Table.Tr>
                  ) : creditNotes.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        No credit notes found
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    creditNotes.map((record) => (
                      <Table.Tr key={record.creditNoteId}>
                        {columns.map((column) => (
                          <Table.Td key={column.key}>
                            {column.render
                              ? column.render(record[column.dataIndex as keyof ICreditNote], record)
                              : record[column.dataIndex as keyof ICreditNote]}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            {pagination.total > pagination.pageSize && (
              <Center mt='md'>
                <Pagination
                  value={pagination.current}
                  onChange={(page) => handleTableChange({ current: page })}
                  total={Math.ceil(pagination.total / pagination.pageSize)}
                  size='sm'
                />
              </Center>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default CreditNotesModal;
